<template>
  <div class="availability-filter">
    <div class="availability-filter__colors">
      <span>
        Цвета
      </span>
      <ui-select
        ref="colors"
        :value="activeColor"
        :options="computedColors"
        :disabled="computedColors.length <= 1"
        class="ui-select-colors"
        placeholder="Выберите цвет"
        @click-input="onClickColorFilter"
        @change="chooseColor"
      />
    </div>

    <div class="availability-filter__sizes">
      <span>
        Размеры
      </span>

      <div class="availability-filter__sizes-wrap">
        <ui-select
          ref="sizes"
          :value="ACTIVE_SKU.id"
          :options="computedSizes"
          class="ui-select-sizes"
          empty-value="Не выбран"
          placeholder="Выберите размер"
          @click-input="onClickSizeFilter"
          @change="chooseSize"
        />

        <div class="availability-filter__not-delayed">
          <ui-checkbutton
            ref="inStore"
            :value="isInStore"
            label="В наличии"
            @input="onInputInStore"
          >
            <f-sprite-icon :icon-link="$options.icons.IconLocation.id" slot="icon" />
          </ui-checkbutton>
        </div>
      </div>
    </div>

    <div
      v-if="CITY.hasMetro"
      class="availability-filter__metro"
    >
      <span>
        Метро
      </span>
      <ui-select
        ref="metro"
        v-model="metro.value"
        :options="metroSpecs"
        class="ui-select-metro"
        empty-value="Не выбрана"
        placeholder="Выберите станцию метро"
        @change="chooseMetro"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { productGtmEvents } from '@/gtm/product';
import { ROUTE_NAMES } from '@/router/route-names';

import IconLocation from '@icons/location.svg';

export default {
  name: 'availability-filter',

  icons: {
    IconLocation,
  },

  props: {
    metroSpecs: {
      type: Array,
      default: () => [],
    },
    isInStore: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      metro: {
        value: '',
        name: '',
      },
    };
  },

  computed: {
    ...mapGetters({
      PRODUCT: 'product/item',
      CITY: 'user/city',
      COLORS: 'product/colors',
      ACTIVE_COLOR: 'product/activeColor',
      ACTIVE_SKU: 'product/activeSku',
      SIZES: 'product/sizes',
    }),

    computedSizes() {
      return this.SIZES.filter(s => s.available).map(s => ({
        ...s,
        name: s.size,
        value: s.id,
      }));
    },

    computedColors() {
      return this.COLORS.map(c => ({
        id: c.modelId,
        color: c.value,
        name: c.name,
        type: c.type,
      }));
    },

    activeColor() {
      return this.COLORS.find(c => c.modelId === this.ACTIVE_COLOR).name;
    },
  },

  watch: {
    ACTIVE_COLOR() { // сбрасываем метро при смене цвета
      this.resetMetro();
    },

    'CITY.name'() { // и при смене города
      this.resetMetro();
    },
  },

  methods: {
    ...mapActions({
      CHOOSE_COLOR: 'product/chooseColor',
    }),

    writeQueryParams(size = {}) {
      this.$router.replace({
        name: ROUTE_NAMES.Product,
        params: { id: this.ACTIVE_COLOR },
        query: size.available ? { 'sku-id': size.id } : {},
      });
    },

    onClickSizeFilter() {
      productGtmEvents.availability.clickSizeFilter({ productCode: this.PRODUCT?.code });
    },

    chooseSize(size) {
      if (size.id === this.ACTIVE_SKU.id) return;
      this.writeQueryParams(size);
      productGtmEvents.availability.chooseSize({
        productCode: this.PRODUCT?.code,
        size: size.size,
      });
      productGtmEvents.card.chooseSize({
        productCode: this.PRODUCT?.code,
        size: size.size,
      });
      this.onChangeFilters('size');
    },

    onClickColorFilter() {
      productGtmEvents.availability.clickColorFilter({ productCode: this.PRODUCT?.code });
    },

    async chooseColor({ id, name } = {}) {
      if (id === this.activeColor?.id) return;

      await this.CHOOSE_COLOR(id);

      productGtmEvents.availability.chooseColor({
        productCode: this.PRODUCT?.code,
        colorName: name,
      });
      productGtmEvents.card.chooseColor({ productCode: this.PRODUCT?.code });
      this.onChangeFilters('color');
    },

    chooseMetro(metro) {
      this.$emit('change', { ...metro });
      this.onChangeFilters('metro');
    },

    resetMetro() {
      this.metro = { name: '', value: '' };
      this.$emit('change', this.metro);
    },

    onInputInStore(value) {
      productGtmEvents.availability.toggleInStore({
        value,
        productCode: this.PRODUCT?.code,
        cityName: this.CITY?.name,
      });
      this.$emit('in-store-change', value);
    },

    async onChangeFilters(key) {
      await this.$nextTick();
      this.$emit('update', key);
    },
  },
};
</script>

<style lang="scss">
.availability-filter {
  margin-top: 24px;
  padding: 12px;
  background-color: $gray_bgr;
  border-radius: 4px;

  &__colors,
  &__sizes,
  &__metro {
    & > span {
      font-size: 12px;
      line-height: 16px;
      color: $gray;
    }
  }

  .ui-select-sizes {
    width: calc(50% - 4px);
  }

  &__sizes-wrap {
    display: flex;
    align-content: center;
  }

  &__sizes,
  &__metro {
    margin-top: 12px;
  }

  .ui-select-colors.disabled {
    input,
    .ui-select-input {
      background-color: #fff;
      box-shadow: inset 0 0 0 1000px #fff;
    }
  }

  .ui-select-colors,
  &__sizes-wrap,
  .ui-select-metro {
    margin-top: 4px;
  }

  &__not-delayed {
    flex: 1 0 auto;
    margin-left: 12px;
  }
}

@include breakpoint("tablet") {
  .availability-filter {
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    &__colors {
      margin-right: 16px;
    }

    &__sizes {
      margin-top: 0;
    }

    &__metro {
      margin-top: 16px;
    }

    &__sizes-wrap {
      margin-top: 0;
    }

    &__colors,
    &__sizes,
    &__metro {
      display: flex;
      align-items: center;

      & > span {
        margin-right: 12px;
      }
    }

    .ui-select-colors {
      width: 200px;
    }

    .ui-select-sizes {
      width: 164px;
    }

    &__metro,
    .ui-select-metro {
      width: 100%;
    }

    .ui-select-colors,
    .ui-select-sizes,
    .ui-select-metro {
      margin-top: 0;
    }

    &__not-delayed {
      margin-left: 16px;
      flex: 0 0 auto;

      .ui-checkbutton {
        padding: 7px 15px;
      }
    }
  }
}

@include breakpoint("desktop") {
  .availability-filter {
    flex-wrap: nowrap;
    padding: 20px;

    &__colors,
    &__sizes,
    &__metro {
      font-size: 14px;
    }

    &__colors {
      margin-top: 0;
    }

    &__metro {
      width: auto;
      margin-top: 0;
      margin-left: auto;
    }

    .ui-select-metro {
      width: 240px;
    }
  }
}
</style>
